import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexNoData,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent
} from 'ng-apexcharts';
import { catchError } from 'rxjs/operators';
import { InlineSvgComponent } from '../../../components/inline-svg/inline-svg.component';
import {
  ExposureHistogramData,
  RiskReport,
  RiskReportAnalysis,
  RiskReportCollectionLineGeometry
} from '../../../models/report.model';
import { RoundPipe } from '../../../pipes/round.pipe';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NewReportService } from '../../../services/newReport.service';
import { NotificationService } from '../../../services/notification.service';
import { getRiskColors } from '../../../utils';
import { MapCommunicatorService } from '../../risk-map/map-communicator.service';
import { HistogramBarComponent } from './histogram-bar/histogram-bar.component';
import { MapComponent } from '../../risk-map/components/map/map.component';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  labels: any;
  plotOptions: ApexPlotOptions;
  legend: ApexLegend;
  colors: string[];
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  fill: ApexFill;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  noData: ApexNoData;
  grid: ApexGrid;
};

@Component({
  selector: 'frmg-report',
  templateUrl: './areaReport.component.html',
  styleUrls: ['./areaReport.component.scss'],
  imports: [
    NgClass,
    InlineSvgComponent,
    ChartComponent,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    MatSlideToggle,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatTooltip,
    HistogramBarComponent,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
    RoundPipe,
    MapComponent
  ]
})
export class AreaReportComponent implements OnInit {
  @Input() id!: string;
  @Input() collectionId!: string;

  @ViewChild('chart') chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;
  public histogramOptions: Partial<ChartOptions>;
  public chartReady = false;

  public displayedColumns = ['name', 'avg_exposure_value'];
  public displayedColumnsDrag: string[] = [];
  public hiddenColumns: string[] = [];
  public reportDataFull: Array<RiskReportCollectionLineGeometry> = [];
  public reportData: Array<RiskReportCollectionLineGeometry> = [];
  public report: RiskReport;
  public totalElements = 0;
  public legends: number[] = [0, 1, 2, 3, 4];
  public colors: string[] = getRiskColors();
  public avgScore: string = '0';
  public threshold: string = '0 - 100';
  public scoreTotal: number = 0;
  public sizeTotal: string = '0 km<sup>2</sup>';
  public areaSearchCtrl: FormControl = new FormControl<string>('');
  public reportNameCtrl: FormControl = new FormControl<string>('');
  public loadingData = signal(false);
  public columnMenu: boolean = false;
  public histogramReady: boolean = false;
  public percentile: string;
  private currentHover: number;
  public currentChart: string = 'bar';

  private readonly destroyRef = inject(DestroyRef);

  // private rawData: any;

  constructor(
    private reportService: NewReportService,
    private router: Router,
    private mapCommunicatorService: MapCommunicatorService,
    private notificationService: NotificationService,
    private cd: ChangeDetectorRef,
    private errorHandlerService: ErrorHandlerService
  ) {
    this.displayedColumnsDrag = this.displayedColumns.slice(1, -1);
  }

  ngOnInit() {
    this.getReport();

    this.chartOptions = {
      series: [
        {
          data: [2, 1, 12, 12, 12, 12, 25, 4],
          name: 'POINT'
        }
      ],
      labels: [
        'No Risk',
        'Low Risk',
        'Medium Risk',
        'High Risk',
        'Very High Risk'
      ],
      chart: {
        type: 'line',
        toolbar: {
          show: false
        },
        height: '100%',
        width: '100%',
        events: {},
        zoom: {
          enabled: false
        },
        redrawOnParentResize: true
      },
      plotOptions: {},
      yaxis: {
        tickAmount: 4,
        forceNiceScale: true,
        decimalsInFloat: 0,
        title: {
          text: 'Exposure'
        }
      },
      xaxis: {
        title: {
          text: 'Segments',
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      fill: {
        colors: ['#5E84F7'],
        opacity: 1
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        show: false
      },
      colors: ['#5E84F7'],
      tooltip: {
        followCursor: false,
        cssClass: 'exposure-tooltip',
        x: {
          show: false,
          formatter: (value) => {
            if (this.currentHover !== value) {
              this.currentHover = value;
              const index = this.reportData.findIndex(
                (segement) => segement.feature_index === value - 1
              );
              if (index !== -1) {
                this.mapCommunicatorService.mapCommunicator.next(
                  `hover_${this.reportData[index].id}_true`
                );
              }
            }
            return `<span style="color:#5E84F7">${value}</span>`;
          }
        },
        y: {
          formatter: (value) => {
            return `<span style="color:#5E84F7">${value.toFixed(0)}</span>`;
          },
          title: {
            formatter() {
              return 'Exposure value';
            }
          }
        },
        style: {
          fontFamily: 'Montserrat'
        }
      },
      stroke: {
        width: 2,
        colors: ['#5E84F7']
      },
      noData: {
        text: 'No Data',
        offsetY: -80,
        style: {
          fontFamily: 'Montserrat',
          fontSize: '20px'
        }
      }
    };
    this.histogramOptions = {
      series: [
        {
          data: [2, 1, 12, 12, 12, 12, 25, 4],
          name: 'POINT'
        }
      ],
      labels: [
        'No Risk',
        'Low Risk',
        'Medium Risk',
        'High Risk',
        'Very High Risk'
      ],
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        height: '110%',
        width: '100%',
        events: {},
        zoom: {
          enabled: false
        },
        redrawOnParentResize: true
      },
      plotOptions: {
        bar: {
          borderRadius: 2,
          borderRadiusApplication: 'end',
          columnWidth: '100%'
        }
      },
      yaxis: {
        tickAmount: 4,
        forceNiceScale: true,
        decimalsInFloat: 0,
        title: {
          text: 'Number of Segments'
        }
      },
      xaxis: {
        title: {
          text: 'Exposure',
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      fill: {
        colors: ['#9B7EDA'],
        opacity: 1
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        show: false
      },
      colors: this.colors,
      tooltip: {
        followCursor: false,
        cssClass: 'exposure-tooltip',
        x: {
          show: false
        },
        y: {
          formatter: (value) => {
            return `<span style="color:#5E84F7">${value}</span>`;
          },
          title: {
            formatter() {
              return 'Number of Segments';
            }
          }
        },
        style: {
          fontFamily: 'Montserrat'
        }
      },
      stroke: {
        width: 1,
        colors: ['#6341AC']
      },
      noData: {
        text: 'No Data',
        offsetY: -80,
        style: {
          fontFamily: 'Montserrat',
          fontSize: '20px'
        }
      },
      grid: {
        padding: {
          left: -10,
          right: -10
        }
      }
    };

    this.areaSearchCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        this.createTableData();
      });
  }

  public focusOn(element: RiskReportAnalysis, event: Event) {
    event.stopPropagation();
    this.mapCommunicatorService.mapCommunicator.next(
      `focus_${element.area.properties.id}_true`
    );
  }

  public highlightOnHover(element: RiskReportCollectionLineGeometry) {
    this.mapCommunicatorService.mapCommunicator.next(
      `hover_${element.id}_true`
    );
  }

  sortData(sort: Sort) {
    this.getReportInfo(sort);
  }

  public selectChart(type: string) {
    this.currentChart = type;
  }

  public getChartData() {
    this.reportService
      .getHistogram(this.id, this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // this.rawData = scores.chart;
        // this.calculateChartData(scores.chart);
      });

    this.reportService
      .getReportCollectionScore(
        this.id,
        this.collectionId
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((scores) => {
        console.log(scores);
        this.calculateHistogramData(scores);
      });
  }

  private calculateHistogramData(scores: ExposureHistogramData) {
    let max = Math.max(...scores.counts);
    max = max + (4 - (max % 4));
    this.histogramOptions.yaxis = {
      tickAmount: 4,
      max,
      min: 0,
      decimalsInFloat: 0,
      labels: {
        offsetY: -5
      },
      title: {
        text: 'Number of Segments',
        style: {
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Montserrat, sans-serif'
        }
      }
    };
    const data = scores.counts;
    let labels = [];
    if (data.length) {
      const series = [{
        data: data.map((d, i) => {
          const startSeg = +scores.bins[i].split('-')[0];
          const pos = scores.bin_edges.findIndex(edge => edge == startSeg);
          return {
            x: pos + 0.5,
            y: d
          };
        }),
        name: 'Exposure Histogram'
      }];
      series[0].data.push({ x: (series[0].data.length + 0.5), y: 0 });
      this.histogramOptions.series = series;
      labels = scores.bin_edges;
      console.log(labels, this.histogramOptions.series);
    } else {
      this.histogramOptions.series = [];
      labels = ['0', '20', '40', '60', '80', '100'];
    }
    const labelsInfo = {
      style: {
        fontFamily: 'Montserrat, sans-serif'
      },
      formatter: (x) => {
        return labels[x];
      }
    };
    this.histogramOptions.xaxis = {
      min: 0,
      max: labels.length || 6,
      tickAmount: labels.length || 6,
      tickPlacement: 'on',
      title: {
        text: 'Exposure',
        offsetY: -10,
        style: {
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Montserrat, sans-serif'
        }
      },
      categories: [],
      labels: labelsInfo
    };

    this.histogramOptions.labels = labelsInfo;
    this.histogramReady = true;
  }

  // private calculateChartData(scores) {
  //   this.chartOptions.yaxis = {
  //     tickAmount: 5,
  //     max: 100,
  //     min: 0,
  //     decimalsInFloat: 0,
  //     title: {
  //       text: 'Exposure',
  //       style: {
  //         fontSize: '14px',
  //         fontWeight: '600',
  //         fontFamily: 'Montserrat, sans-serif'
  //       }
  //     }
  //   };
  //   const data = scores.data.datasets;
  //   let labels = [];
  //   if (data.length) {
  //     this.chartOptions.series = data.map((sr) => {
  //       return {
  //         ...sr,
  //         data: sr.data.map((d, i) => ({
  //           x: `${scores.data.segmentMetadata[i]?.segment}`,
  //           y: d
  //         })),
  //         name: sr.label
  //       };
  //     });
  //     labels = scores.data.labels;
  //   } else {
  //     this.chartOptions.series = [];
  //     labels = [];
  //   }
  //   const labelsInfo = {
  //     style: {
  //       fontFamily: 'Montserrat, sans-serif'
  //     },
  //     formatter: (x) => {
  //       return (
  //         (x &&
  //           this.rawData.data.segmentMetadata.find((el) => el.segment == x)
  //             ?.distance_to) ||
  //         ''
  //       );
  //     }
  //   };
  //   this.chartOptions.xaxis = {
  //     max: labels.length || 6,
  //     tickAmount: labels.length < 15 ? labels.length : 15,
  //     tickPlacement: 'on',
  //     title: {
  //       text: 'Distance (km)',
  //       style: {
  //         fontSize: '14px',
  //         fontWeight: '600',
  //         fontFamily: 'Montserrat, sans-serif'
  //       }
  //     },
  //     categories: [],
  //     labels: labelsInfo,
  //     tooltip: {
  //       enabled: false
  //     }
  //   };
  //   this.chartOptions.labels = labelsInfo;
  //   this.chartReady = true;
  // }

  private getReportInfo(_sort?: Sort): void {
    this.loadingData.set(true);
    const config: any = { page: 1, size: 100 };
    this.reportService
      .getReportAnalyseById(this.id, this.collectionId, config)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(this.errorHandlerService.handle404And422('/report-table'))
      )
      .subscribe((result) => {
        this.reportService.breadCrumbs.next([result.collection_metadata.area_name]);
        this.reportService.getReportCollectionGeometries(this.id, this.collectionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(geometries => {
          this.totalElements = geometries.total;
          const details = geometries.items;
          details.sort(
            (a, b) =>
              a.feature_index - b.feature_index
          );
          // this.percentile = `${(
          //   details
          //     .map((data) => data.area.properties.percentile)
          //     .reduce(this.add, 0) / details.length
          // ).toFixed(0)}%`;
          this.reportDataFull = details;
          this.createTableData();
          this.loadingData.set(false);
        });
      });
  }

  private createTableData(): void {
    this.reportData = this.reportDataFull;
  }

  private getReport(): void {
    this.loadingData.set(true);
    this.reportService
      .getReport(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((report) => {
        this.report = report;
        this.reportNameCtrl.setValue(this.report.name);
        this.threshold = `${this.report.lowest_threshold} - ${this.report.highest_threshold}`;
        this.getReportInfo();
      });
    this.getChartData();
  }

  public downloadArea(_: number, filename: string = 'report'): void {
    this.reportService
      .getAreaReportPdf(this.id, this.collectionId, filename)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.notificationService.updateDownload({
          fileName: `${filename}.pdf`,
          percentage: data.progress,
          total: data.total,
          loaded: data.loaded
        });
      });
  }

  public downloadAreaXLSX(_: number, filename: string = 'report'): void {
    this.reportService
      .getAreaReportXLSX(this.id, this.collectionId, filename)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.notificationService.updateDownload({
          fileName: `${filename}.xlsx`,
          percentage: data.progress,
          total: data.total,
          loaded: data.loaded
        });
      });
  }

  public openAnalysisModal(analysis: RiskReportCollectionLineGeometry) {
    this.router.navigate([
      `report/${this.id}/collection/${this.collectionId}/details/${analysis.id}`
    ]);
  }

  public openAnalysisModalById(analysisId: string) {
    const analysis = this.reportData.find(
      (analysis) => analysis.id === analysisId
    );
    if (analysis && analysis.geometry_data.type == 'Point')
      this.openAnalysisModal(analysis);
  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
    this.displayedColumns = ['name', ...this.displayedColumnsDrag, 'action'];
  }

  public toggleColumn(index: number, column: string, show: boolean) {
    if (show) {
      this.hiddenColumns.push(column);
      this.displayedColumnsDrag.splice(index, 1);
    } else {
      this.displayedColumnsDrag.push(column);
      this.hiddenColumns.splice(index, 1);
    }
    this.displayedColumns = ['name', ...this.displayedColumnsDrag, 'action'];
    this.cd.detectChanges();
  }

  public toggleColumnMenu() {
    this.columnMenu = !this.columnMenu;
  }

  public segmentLength(prop: RiskReportCollectionLineGeometry): string {
    // if (prop.to_distance) {
    //   const from = ((prop.from_distance + 2000) / 1000).toFixed(0);
    //   const to = ((prop.to_distance + 2000) / 1000).toFixed(0);
    //   return `Segment from ${from}km to ${to}km`;
    // } else {
    return `Segment ${prop.feature_index}`;
  }

  // }
}
